import { hydrateRoot } from 'react-dom/client';
import { HydratedRouter } from 'react-router/dom';
import { AppName } from './configuration';
import { StrictMode, startTransition } from 'react';
import { env } from '~/platform/platform';
import {
  firstMarkupDivergence,
  hasTranslationMarkers,
} from '~/lib/hydration-diff';
import { reportError, startRum } from '~/lib/rum';
import { captureUReferrer } from '~/domain/plugins/services/tracker/persist-campaign.client';

const RUM_ENDPOINT = env.DASH0_RUM_ENDPOINT;
const RUM_TOKEN = env.DASH0_RUM_TOKEN;

if (RUM_ENDPOINT && RUM_TOKEN) {
  // The trace that served this document, read off the parsed markup rather
  // than loader data and handed to init rather than added after it, so it is
  // on every signal from the first: the page view init itself sends, and the
  // LCP it reports from buffered entries straight after — both of which can
  // precede the tenant attributes registered in the plugins provider.
  //
  // `app.trace_id` is what joins a RUM signal to the server work behind it. A
  // slow LCP otherwise names a page and a tenant and stops there, so whether
  // the slow tail comes from a loader, an upstream call or the client has to be
  // argued from circumstance instead of followed.
  const { traceId } = document.documentElement.dataset;

  // A dynamic import keeps the SDK out of the preloaded JavaScript of pages
  // that don't bring it along themselves; see ~/lib/rum. Started here, before
  // hydrateRoot, because what happens before init is only partly recoverable.
  void startRum(() => import('@dash0/sdk-web'), {
    serviceName: AppName,
    serviceVersion: __APP_RELEASE__,
    environment: __APP_ENV__,
    endpoint: {
      url: RUM_ENDPOINT,
      authToken: RUM_TOKEN,
    },
    additionalSignalAttributes: {
      ...(traceId && { 'app.trace_id': traceId }),
    },
    propagators: [
      {
        type: 'traceparent',
        match: [/^https?:\/\/[^/]*\.understory\.io/, /^https?:\/\/localhost/],
      },
    ],
    ignoreUrls: [
      /^https?:\/\/([^/]+\.)?amplitude\.com/,
      // Google ads/remarketing endpoints — frequently blocked by adblockers,
      // resulting in noisy network error reports we don't act on.
      /^https?:\/\/([^/]+\.)?googlesyndication\.com/,
      /^https?:\/\/([^/]+\.)?google\.com\/(ccm|rmkt|pagead)\//,
      /^https?:\/\/([^/]+\.)?doubleclick\.net\/pagead\//,
      // React Router action/loader data requests. 4xx responses here are
      // form-validation rejections (the action returns
      // `data({ ok: false, errors }, { status: 400 })`), not real failures.
      // 5xx is still observable via server-side function logs.
      /\.data($|\?)/,
      // React Router "fog of war" route-discovery endpoint. The server
      // returns 204 (X-Remix-Reload-Document) whenever the client's build
      // version is stale — expected deploy-version skew, not a failure. The
      // client's manifest fetcher throws on the bodyless 204, which we don't
      // act on. Genuine 500s remain observable via the server-side
      // `handleError` (entry.server.tsx) → logger.error path.
      /\/__manifest($|\?)/,
    ],
    ignoreErrorMessages: [
      /Object Not Found Matching Id:/,
      /^TypeError: cancelled$/,

      // Aborted fetches — Safari cancels in-flight requests when the user
      // navigates away mid-load; 185 events / 74 distinct paths in 48h,
      // almost all Safari 26.x. The SDK reports these bare (`Load failed`),
      // as an `Error.message` (`TypeError: Load failed`), or wrapped
      // (`Unhandled promise rejection: Load failed`) — never with a single
      // fixed prefix — so the patterns below drop the `^` anchor and match
      // on the trailing phrase instead. (The previous `^TypeError: …$`
      // patterns for these two never matched anything for exactly that
      // reason.) `Failed to fetch` and `Load failed` are unqualified by
      // nature — we accept that a real network failure could share the
      // message, because 5xx/loader failures remain independently
      // observable server-side (see the `.data` and `/__manifest`
      // `ignoreUrls` comments above).
      /Failed to fetch$/,
      /Load failed$/,
      /NetworkError when attempting to fetch resource\.$/,
      /BodyStreamBuffer was aborted$/,

      // In-app WebView and browser-extension bridge noise — third-party
      // bridge code we don't control and can't fix; 105 events in 48h.
      /Java exception was raised during method invocation/, // Facebook/Instagram in-app browser (Android) postMessage bridge — 74 events
      /WKWebView API client did not respond to this postMessage/, // iOS in-app WebView bridge
      /Can't find variable: SCDynimacBridge/, // Samsung Internet browser bridge
      /Can't find variable: __firefox__/, // Firefox iOS content-blocker bridge
      /Invalid call to runtime\.sendMessage\(\)\. Tab not found\./, // browser extension messaging
      /Cannot destructure property 'tabId' from null or undefined value/, // browser extension messaging
      /No Listener: tabs:outgoing\.message\.ready/, // browser extension messaging

      // Third-party CDN/script failures we don't control; 14 events in 48h.
      // Host- (or tag-) qualified so a real dynamic-import or script-load
      // failure of our own still reports.
      /Failed to fetch dynamically imported module: https:\/\/consent-eu\.cookiefirst\.com\//,
      /\[CF\] Failed to load banner styles/,
      /\[CF\] Failed to load configs, check API key/,
      /Error loading: https:\/\/sleeknotestaticcontent\.sleeknote\.com\//,

      // Deploy-version skew — old HTML asking for build assets a newer
      // deploy has already removed; 20 events in 48h. `Unable to preload
      // CSS for ...` is path-scoped to our own `/assets/`, so it can't
      // swallow an unrelated CSS failure. `Importing a module script
      // failed.` can't be scoped the same way — the browser gives no URL
      // in this message — making it a third unqualified-by-nature
      // exception alongside `Failed to fetch` / `Load failed` above: a
      // genuine dynamic-import failure in our own bundle (not caused by
      // deploy skew) would also be suppressed. Accepted because a truly
      // broken bundle deploy announces itself through collapsed page views
      // and a spike in other errors, not through this one string.
      /Importing a module script failed\.$/,
      /Unable to preload CSS for \/assets\//,

      // Universally benign browser quirk (the observer's own callback ran
      // long enough that a notification round got dropped) — never
      // actionable; 18 events / 16 sessions / 14 paths in 48h. Contrast
      // with the `removeChild` NotFoundError from #418 recovery, which IS
      // a real consequence of a hydration mismatch and must keep
      // reporting.
      /ResizeObserver loop completed with undelivered notifications\./,
    ],
  });
}

try {
  captureUReferrer();
} catch (error) {
  reportError(error instanceof Error ? error : { message: String(error) });
}

// React 19 reports a hydration mismatch (#418) with the component stack of the
// element that mismatched, but not the mismatch itself (stripped from minified
// errors). To see WHAT mismatched in production, snapshot the server-rendered
// markup before hydration and diff it against the DOM after React has
// recovered by client rendering (US-11224). The snapshot is released once
// hydration has settled — recoverable #418s only fire during initial hydration.
let ssrBodyHtml: string | null = document.body?.innerHTML ?? null;
setTimeout(() => {
  ssrBodyHtml = null;
}, 30_000);

// As a transition, hydration yields to the browser every few milliseconds
// instead of holding the main thread for the whole page — 0.4–1 s on a
// throttled phone, the bulk of the storefront's Total Blocking Time. React
// replays a click that lands mid-hydration once that part has hydrated.
startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <HydratedRouter />
    </StrictMode>,
    {
      // Forward hydration mismatches and other recoverable React errors to
      // Dash0. For #418, attach where the server markup and the recovered
      // client render first diverge — that names the mismatched content, which
      // React itself won't (see the snapshot comment above).
      onRecoverableError(error, errorInfo) {
        const errorLike =
          error instanceof Error
            ? error
            : { message: typeof error === 'string' ? error : String(error) };

        let attributes: Record<string, string> | undefined;
        if (errorLike.message.includes('#418')) {
          const clientBodyHtml = document.body?.innerHTML ?? '';
          if (ssrBodyHtml !== null) {
            const divergence = firstMarkupDivergence(
              ssrBodyHtml,
              clientBodyHtml
            );
            if (divergence) {
              attributes = {
                // `ssr`/`client` are absent when every divergence found was an
                // empty Suspense skeleton (US-12214) — nothing left to report
                // once those are skipped. Presence/absence of these two keys,
                // together with `skipped` below, is enough to tell that case
                // apart from a real divergence found straight away or only
                // after skipping past one or more skeletons.
                ...(divergence.ssr !== undefined &&
                divergence.client !== undefined
                  ? {
                      'hydration.mismatch.ssr': divergence.ssr,
                      'hydration.mismatch.client': divergence.client,
                    }
                  : {}),
                ...(divergence.skipped > 0
                  ? { 'hydration.mismatch.skipped': String(divergence.skipped) }
                  : {}),
              };
            }
          }

          // In-browser page translators rewrite the DOM between SSR and
          // hydration, making #418 inevitable — a third of diff-bearing events.
          // Tag them so Dash0 views and alerts can exclude the expected noise
          // without losing sight of the volume. Google Translate flags the root
          // element; Edge's translator only marks the elements it touched (see
          // hasTranslationMarkers).
          const html = document.documentElement;
          if (
            html.classList.contains('translated-ltr') ||
            html.classList.contains('translated-rtl') ||
            hasTranslationMarkers(ssrBodyHtml ?? '') ||
            hasTranslationMarkers(clientBodyHtml)
          ) {
            attributes = {
              ...attributes,
              'hydration.cause': 'machine-translation',
            };
          }
        }

        reportError(errorLike, {
          componentStack: errorInfo.componentStack,
          attributes,
        });
      },
    }
  );
});
